import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import React from 'react';

import KestrixCloseButton from './buttons/KestrixCloseButton.jsx';
import { colors } from './KestrixDesignSystem.jsx';
import KestrixTypography from './KestrixTypography.jsx';

/**
 * KestrixModal - Reusable base modal component
 * 
 * This component provides a consistent modal structure that can be extended
 * for specific use cases like dialogs, progress modals, etc.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to call when modal is closed
 * @param {string} [props.title] - Modal title
 * @param {string} [props.subtitle] - Modal subtitle
 * @param {React.ReactNode} [props.children] - Modal content
 * @param {string} [props.size='medium'] - Modal size: 'small', 'medium', 'large', 'full'
 * @param {boolean} [props.showCloseButton=true] - Whether to show close button
 * @param {string} [props.closeButtonVariant='modal'] - Close button variant
 * @param {string} [props.closeButtonPosition='top-right'] - Close button position
 * @param {Object} [props.sx] - Additional styles for the modal
 * @param {string} [props.dataTestId] - Test ID for the modal
 * @returns {JSX.Element} KestrixModal component
 */
const KestrixModal = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  size = 'medium',
  showCloseButton = true,
  closeButtonVariant = 'modal',
  closeButtonPosition = 'top-right',
  sx = {},
  dataTestId,
  ...props
}) => {
  // Size configurations
  const sizeConfig = {
    small: { width: 320, minHeight: 200 },
    medium: { width: 448, minHeight: 242 },
    large: { width: 600, minHeight: 400 },
    full: { width: '90vw', height: '90vh' }
  };

  const currentSize = sizeConfig[size] || sizeConfig.medium;

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        ...sx
      }}
      data-testid={dataTestId}
      {...props}
    >
      <Paper
        sx={{
          width: currentSize.width,
          minHeight: currentSize.minHeight,
          maxWidth: '95vw',
          maxHeight: '95vh',
          padding: 3,
          borderRadius: 2,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          border: `1px solid ${colors.border.light}`
        }}
      >
        {/* Close button */}
        {showCloseButton && (
          <KestrixCloseButton
            variant={closeButtonVariant}
            size="medium"
            position={closeButtonPosition}
            onClick={onClose}
            sx={{
              position: 'absolute',
              zIndex: 1
            }}
          />
        )}

        {/* Header section */}
        {(title || subtitle) && (
          <Box sx={{ mb: 3, pr: showCloseButton ? 4 : 0 }}>
            {title && (
              <KestrixTypography
                variant="h6"
                sx={{
                  fontWeight: 'semibold',
                  color: colors.text.primary,
                  mb: subtitle ? 1 : 0
                }}
              >
                {title}
              </KestrixTypography>
            )}
            {subtitle && (
              <KestrixTypography
                variant="body1"
                sx={{
                  color: colors.text.secondary
                }}
              >
                {subtitle}
              </KestrixTypography>
            )}
          </Box>
        )}

        {/* Content section */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {children}
        </Box>
      </Paper>
    </Modal>
  );
};

export default KestrixModal;
